// src/components/templates/ArticlePageTemplate/ArticlePageTemplate.jsx
import React, { useRef, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, useTheme, useMediaQuery, IconButton, Drawer, Badge, Button, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment } from '@mui/material';
import {
  TuneRounded as TuneIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import MainToolbar from '../../organisms/MainToolbar/MainToolbar';
import FilterDialog from '../../organisms/FilterDialog/FilterDialog';
import SelectionDialog from '../../organisms/SelectionDialog/SelectionDialog';
import SelectionToolbar from '../../organisms/SelectionToolbar/SelectionToolbar';
import { colors } from '../../../design-system/tokens/colors';
import { useAppBarActions } from '../../../context/AppBarActionsContext';
import BottomSheetHandle from '../../atoms/BottomSheetHandle';
import HeaderPrimaryAction from '../../atoms/HeaderPrimaryAction';
// import ActiveFiltersBar from '../../molecules/ActiveFiltersBar/ActiveFiltersBar'; // REMOVIDO - barra antiga

/**
 * ArticlePageTemplate - Template para páginas de artigos
 * 
 * Este componente define a estrutura comum para todas as páginas de artigos.
 * Segue o princípio do Atomic Design como um "template".
 */
const ArticlePageTemplate = ({
  title,
  subtitle,
  iconType,

  // Estado e callbacks da MainToolbar
  searchValue,
  onSearch,
  selectionMode,
  onToggleSelectionMode,
  filtersOpen,
  onToggleFilters,
  sortBy,
  onSortChange,
  sortOptions,
  cardsPerPage,
  onCardsPerPageChange,
  variant,
  onVariantChange,
  onNewArticle,

  // Estatísticas para MainToolbar
  totalItems = 0,
  filteredItems = 0,
  selectedItems = 0,

  // Props para filtro de período
  timePeriod,
  onTimePeriodChange,
  quickDateType,
  onQuickDateTypeChange,
  totalCount,
  customDateRange,
  
  // QuickFilters props
  showFavoritesOnly,
  onToggleFavorites,

  // Estado e callbacks dos AdvancedFilters
  dateValue,
  onDateChange,
  dateOptions,
  selectedSources,
  onSourcesChange,
  sourceOptions,
  selectedSentiments,
  onSentimentsChange,
  sentimentOptions,
  selectedExposures,
  onExposuresChange,
  exposureOptions,
  selectedTags,
  onTagsChange,
  tagOptions,
  selectedCollections,
  onCollectionsChange,
  collectionOptions,
  selectedMediaTypes,
  onMediaTypesChange,
  mediaTypeOptions,
  selectedContentTypes,
  onContentTypesChange,
  contentTypeOptions,
  selectedTiers,
  onTiersChange,
  tierOptions,
  selectedStatuses,
  onStatusesChange,
  statusOptions,
  selectedEntityType,
  onEntityTypeChange,
  entityTypeOptions,
  showTagFilter,
  showCollectionFilter,
  onClearFilters,

  // Props para filtros facetados
  facets,
  facetsLoading,
  
  // ActiveFiltersIndicator props
  activeFilters,
  onRemoveFilter,
  activeFiltersExpanded,
  onToggleActiveFiltersExpanded,
  
  // Estado e callbacks do SelectionToolbar
  onSelectAll,
  onEditFields,
  onNativeReview,
  onFavorite,
  onAddTag,
  onAddToCollection,
  onRemoveFromCollections,
  onSetSentiment,
  onSetRead,
  onDelete,
  onSendNewsletter,
  onBulkRescrapeSmart,
  onBulkAnalyzeKpia,
  onClearSelection,
  onExportExcel,
  selectedCount = 0,
  allSelected = false,
  showRemoveFromTag,
  onRemoveFromTag,
  showRemoveFromCollection,
  onRemoveFromCollection,
  onRemoveTags,

  // Props para seleção estilo Gmail
  currentPageCount,
  onSelectAllPages,
  selectingAllPages,
  bulkActionLoading,
  bulkActionProgress,
  exportingExcel,

  // Conteúdo principal
  loading,
  error,
  children,

  className = '',
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(64);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [mobileToolbarExpanded, setMobileToolbarExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { setAppBarActions, clearAppBarActions } = useAppBarActions();

  // Calcular número de filtros ativos para mostrar no badge
  const activeFiltersCount = (activeFilters?.length || 0) +
    (selectedMediaTypes?.length || 0) +
    (selectedContentTypes?.length || 0) +
    (selectedTiers?.length || 0) +
    (selectedSentiments?.length || 0) +
    (selectedEntityType ? 1 : 0);

  const desktopToolbarPanel = useMemo(() => (
    <MainToolbar
      title={title}
      subtitle={subtitle}
      iconType={iconType}
      searchValue={searchValue}
      onSearch={onSearch}
      selectionMode={selectionMode}
      onToggleSelectionMode={onToggleSelectionMode}
      filtersOpen={filtersOpen}
      onToggleFilters={onToggleFilters}
      activeFiltersCount={activeFiltersCount}
      sortBy={sortBy}
      onSortChange={onSortChange}
      sortOptions={sortOptions}
      cardsPerPage={cardsPerPage}
      onCardsPerPageChange={onCardsPerPageChange}
      variant={variant}
      onVariantChange={onVariantChange}
      totalItems={totalItems || 0}
      filteredItems={filteredItems || 0}
      selectedItems={selectedItems || 0}
      totalCount={totalCount}
      timePeriod={timePeriod}
      quickDateType={quickDateType}
      onQuickDateTypeChange={onQuickDateTypeChange}
      onTimePeriodChange={onTimePeriodChange}
      customDateRange={customDateRange}
      compactSearch
      selectedMediaTypes={selectedMediaTypes}
      onMediaTypesChange={onMediaTypesChange}
      mediaTypeOptions={mediaTypeOptions}
      selectedContentTypes={selectedContentTypes}
      onContentTypesChange={onContentTypesChange}
      contentTypeOptions={contentTypeOptions}
      selectedTiers={selectedTiers}
      onTiersChange={onTiersChange}
      tierOptions={tierOptions}
      selectedSentiments={selectedSentiments}
      onSentimentsChange={onSentimentsChange}
      sentimentOptions={sentimentOptions}
      selectedEntityType={selectedEntityType}
      onEntityTypeChange={onEntityTypeChange}
      entityTypeOptions={entityTypeOptions}
      facets={facets}
      facetsLoading={facetsLoading}
    />
  ), [
    title, subtitle, iconType, searchValue, onSearch, selectionMode,
    onToggleSelectionMode, filtersOpen, onToggleFilters, activeFiltersCount,
    sortBy, onSortChange, sortOptions, cardsPerPage, onCardsPerPageChange,
    variant, onVariantChange, onNewArticle, totalItems, filteredItems,
    selectedItems, totalCount, timePeriod, quickDateType,
    onQuickDateTypeChange, onTimePeriodChange, customDateRange, selectedMediaTypes,
    onMediaTypesChange, mediaTypeOptions, selectedContentTypes,
    onContentTypesChange, contentTypeOptions, selectedTiers, onTiersChange,
    tierOptions, selectedSentiments, onSentimentsChange, sentimentOptions,
    selectedEntityType, onEntityTypeChange, entityTypeOptions,
    facets, facetsLoading
  ]);

  useEffect(() => {
    if (isMobile) {
      clearAppBarActions();
      return undefined;
    }

    setAppBarActions({
      activeFiltersCount,
      primaryAction: onNewArticle ? {
        label: 'Artigos',
        onClick: onNewArticle
      } : null,
      period: onTimePeriodChange ? {
        value: timePeriod || 'all',
        quickDateType,
        onChange: onTimePeriodChange,
        onQuickDateTypeChange
      } : null,
      panel: desktopToolbarPanel
    });

    return () => clearAppBarActions();
  }, [
    isMobile,
    activeFiltersCount,
    onNewArticle,
    onTimePeriodChange,
    quickDateType,
    timePeriod,
    desktopToolbarPanel,
    setAppBarActions,
    clearAppBarActions
  ]);

  // Detectar scroll para aplicar efeito glassmorphism
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Função para atualizar altura do header
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    // Atualizar imediatamente
    updateHeight();

    // Atualizar após a animação do Collapse (300ms)
    const timer = setTimeout(updateHeight, 350);

    return () => clearTimeout(timer);
  }, [mobileToolbarExpanded, isMobile]);

  // Detectar mudanças no estado do sidebar
  useEffect(() => {
    const checkSidebarState = () => {
      // Verificar se existe um elemento com classe que indica o estado do sidebar
      const layoutElement = document.querySelector('.layout-with-open-sidebar, .layout-with-closed-sidebar');
      if (layoutElement) {
        setSidebarOpen(layoutElement.classList.contains('layout-with-open-sidebar'));
      }
    };

    // Verificar inicialmente
    checkSidebarState();

    // Observar mudanças no DOM
    const observer = new MutationObserver(checkSidebarState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });

    return () => observer.disconnect();
  }, []);

  // Largura do sidebar baseada no breakpoint
  
  // Margem/padding que o conteúdo principal usa
  
  // Debug: log dos valores

  return (
    <Box className={`article-page-template ${className}`} {...props}>
      {/* Mobile mantem a toolbar recolhivel local; no desktop ela vai para o AppBar. */}
      {isMobile && (
      <Box 
        ref={headerRef}
        sx={{
          position: 'fixed',
          top: 64, // Abaixo do AppBar principal (altura padrão do AppBar)
          left: 0, // Começa do início da tela
          right: 0, // Vai até o fim da tela
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1, // Reduzido de 2 para 1 - Espaçamento entre as barras
          backgroundColor: 'transparent', // Transparente para não interferir
          // Usar margem dinâmica baseada no estado do sidebar
          marginLeft: isMobile ? '0px' : (sidebarOpen ? '240px' : '56px'), // drawerWidth ou miniDrawerWidth
          transition: 'margin-left 0.3s ease', // Mesma transição do page-layout__content
          // Aplicar o mesmo padding que o page-layout__content
          paddingLeft: '16px',
          paddingRight: '16px',
          pointerEvents: isMobile ? 'auto' : 'none',
        }}
      >
        {/* Barra 1: Título e MainToolbar */}
        <Box
          sx={{
            backgroundColor: isMobile
              ? (scrolled
                ? (theme.palette.mode === 'dark'
                  ? colors.glass.background.darkPaper
                  : colors.glass.background.lightPaper)
                : theme.palette.background.paper)
              : 'transparent',
            backdropFilter: isMobile && scrolled ? 'blur(20px)' : 'none',
            WebkitBackdropFilter: isMobile && scrolled ? 'blur(20px)' : 'none',
            boxShadow: isMobile && scrolled ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none',
            borderRadius: 2,
            overflow: isMobile ? 'hidden' : 'visible',
            transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease'
          }}
        >
          {/* Mobile: Barra compacta retrátil */}
          {isMobile ? (
            <>
              {/* Barra compacta sempre visível */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  gap: 1
                }}
              >
                {/* Título */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {title}
                </Typography>

                {/* Contador de artigos */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: 'nowrap', flex: 1 }}
                >
                {filteredItems} <span translate="no">artigos</span>
                </Typography>

                {/* Botão + Artigos (sempre visível no mobile) */}
                {onNewArticle && (
                  <HeaderPrimaryAction
                    label="Adicionar artigos"
                    ariaLabel="Adicionar artigo"
                    onClick={onNewArticle}
                  />
                )}

                {/* Botão de expandir/retrair filtros */}
                <IconButton
                  size="small"
                  onClick={() => setMobileToolbarExpanded(true)}
                  aria-label="Abrir opções dos artigos"
                  sx={{
                    bgcolor: activeFiltersCount > 0
                      ? theme.palette.primary.main
                      : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                    color: activeFiltersCount > 0 ? 'white' : theme.palette.text.primary,
                    '&:hover': {
                      bgcolor: activeFiltersCount > 0
                        ? theme.palette.primary.dark
                        : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Badge
                    badgeContent={activeFiltersCount}
                    color="error"
                    max={9}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.65rem',
                        height: 16,
                        minWidth: 16
                      }
                    }}
                  >
                    <TuneIcon sx={{ fontSize: '1.2rem' }} />
                  </Badge>
                </IconButton>
              </Box>

              {/* Toolbar completa expansível */}
              <Drawer
                anchor="bottom"
                open={mobileToolbarExpanded}
                onClose={() => setMobileToolbarExpanded(false)}
                PaperProps={{ sx: { maxHeight: '88dvh', borderRadius: '18px 18px 0 0', px: 2, pt: 1.25, pb: 'calc(20px + env(safe-area-inset-bottom, 0px))' } }}
              >
                <BottomSheetHandle sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 850 }}>Opções dos artigos</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Filtros, organização e ações</Typography>
                  </Box>
                  <IconButton onClick={() => setMobileToolbarExpanded(false)} aria-label="Fechar opções">
                    <TuneIcon />
                  </IconButton>
                </Box>
                <Typography sx={{ mb: 1, fontSize: '0.7rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>
                  Filtrar e organizar
                </Typography>
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={searchValue}
                    onChange={(event) => onSearch(event.target.value)}
                    placeholder="Pesquisar artigos..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>
                      )
                    }}
                  />
                  {onTimePeriodChange && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Período</InputLabel>
                      <Select value={timePeriod || 'all'} label="Período" onChange={onTimePeriodChange}>
                        <MenuItem value="1d">Último dia</MenuItem>
                        <MenuItem value="3d">Últimos 3 dias</MenuItem>
                        <MenuItem value="7d">Últimos 7 dias</MenuItem>
                        <MenuItem value="30d">Últimos 30 dias</MenuItem>
                        <MenuItem value="all">Todo o período</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  <FormControl fullWidth size="small">
                    <InputLabel>Ordenar por</InputLabel>
                    <Select value={sortBy} label="Ordenar por" onChange={onSortChange}>
                      {(sortOptions || []).map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => { setMobileToolbarExpanded(false); onToggleFilters(); }}
                    sx={{ minHeight: 44, justifyContent: 'flex-start', textTransform: 'none', fontWeight: 700 }}
                  >
                    Filtros avançados{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
                  </Button>
                </Box>
              </Drawer>
            </>
          ) : null}
        </Box>

        {/* Barra 3: ActiveFiltersBar - REMOVIDA (funcionalidades já estão no QuickFilters) */}
        {/* {activeFilters && activeFilters.length > 0 && (
          <ActiveFiltersBar
            activeFilters={activeFilters}
            onEditFilters={onToggleFilters}
            onClearFilters={onClearFilters}
            onRemoveFilter={onRemoveFilter}
            expanded={activeFiltersExpanded}
            onToggleExpand={onToggleActiveFiltersExpanded}
          />
        )} */}
      </Box>

      )}

      {/* Conteúdo principal (lista de artigos) */}
      <Box sx={{ paddingTop: isMobile ? `${headerHeight}px` : 0 }}> {/* Sem espaço extra - cards colados na barra */}
        {children}
      </Box>

      {/* SelectionToolbar - Aparece fixo na parte inferior quando está em modo seleção */}
      <SelectionToolbar
        selectedCount={selectedCount}
        allSelected={allSelected}
        selectionMode={selectionMode && variant !== 'measurement'}
        onToggleSelectionMode={onToggleSelectionMode}
        onSelectAll={onSelectAll}
        onEditFields={onEditFields}
        onNativeReview={onNativeReview}
        onFavorite={onFavorite}
        onAddTag={onAddTag}
        onAddToCollection={onAddToCollection}
        onRemoveFromCollections={onRemoveFromCollections}
        onSetSentiment={onSetSentiment}
        onSetRead={onSetRead}
        onDelete={onDelete}
        onSendNewsletter={onSendNewsletter}
        onBulkRescrapeSmart={onBulkRescrapeSmart}
        onBulkAnalyzeKpia={onBulkAnalyzeKpia}
        onClearSelection={onClearSelection}
        onExportExcel={onExportExcel}
        showRemoveFromTag={showRemoveFromTag}
        onRemoveFromTag={onRemoveFromTag}
        showRemoveFromCollection={showRemoveFromCollection}
        onRemoveFromCollection={onRemoveFromCollection}
        onRemoveTags={onRemoveTags}
        // Props para seleção estilo Gmail
        totalCount={totalCount}
        currentPageCount={currentPageCount}
        onSelectAllPages={onSelectAllPages}
        selectingAllPages={selectingAllPages}
        bulkActionLoading={bulkActionLoading}
        bulkActionProgress={bulkActionProgress}
      />

      {/* Modal de Filtros */}
      <FilterDialog
        open={filtersOpen}
        onClose={onToggleFilters}
        onClearFilters={onClearFilters}
        dateValue={dateValue}
        onDateChange={onDateChange}
        dateOptions={dateOptions}
        selectedSources={selectedSources}
        onSourcesChange={onSourcesChange}
        sourceOptions={sourceOptions}
        selectedSentiments={selectedSentiments}
        onSentimentsChange={onSentimentsChange}
        sentimentOptions={sentimentOptions}
        selectedExposures={selectedExposures}
        onExposuresChange={onExposuresChange}
        exposureOptions={exposureOptions}
        selectedTags={selectedTags}
        onTagsChange={onTagsChange}
        tagOptions={tagOptions}
        selectedCollections={selectedCollections}
        onCollectionsChange={onCollectionsChange}
        collectionOptions={collectionOptions}
        selectedMediaTypes={selectedMediaTypes}
        onMediaTypesChange={onMediaTypesChange}
        mediaTypeOptions={mediaTypeOptions}
        selectedContentTypes={selectedContentTypes}
        onContentTypesChange={onContentTypesChange}
        contentTypeOptions={contentTypeOptions}
        selectedTiers={selectedTiers}
        onTiersChange={onTiersChange}
        tierOptions={tierOptions}
        selectedStatuses={selectedStatuses}
        onStatusesChange={onStatusesChange}
        statusOptions={statusOptions}
        selectedEntityType={selectedEntityType}
        onEntityTypeChange={onEntityTypeChange}
        entityTypeOptions={entityTypeOptions}
        showTagFilter={showTagFilter}
        showCollectionFilter={showCollectionFilter}
      />

      {/* Modal de Seleção */}
      <SelectionDialog
        open={selectionDialogOpen}
        onClose={() => setSelectionDialogOpen(false)}
        selectedCount={selectedCount}
        allSelected={allSelected}
        onSelectAll={onSelectAll}
        onEditFields={onEditFields}
        onNativeReview={onNativeReview}
        onFavorite={onFavorite}
        onAddTag={onAddTag}
        onAddToCollection={onAddToCollection}
        onSetSentiment={onSetSentiment}
        onSetRead={onSetRead}
        onDelete={onDelete}
        onClearSelection={() => {
          onClearSelection();
          setSelectionDialogOpen(false);
        }}
        onExportExcel={onExportExcel}
        exportingExcel={exportingExcel}
        showRemoveFromTag={showRemoveFromTag}
        onRemoveFromTag={onRemoveFromTag}
        showRemoveFromCollection={showRemoveFromCollection}
        onRemoveFromCollection={onRemoveFromCollection}
      />
    </Box>
  );
};

ArticlePageTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  // MainToolbar props
  searchValue: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  selectionMode: PropTypes.bool.isRequired,
  onToggleSelectionMode: PropTypes.func.isRequired,
  filtersOpen: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  sortOptions: PropTypes.array,
  cardsPerPage: PropTypes.number.isRequired,
  onCardsPerPageChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['compact', 'detailed', 'list', 'measurement']).isRequired,
  onVariantChange: PropTypes.func.isRequired,
  onNewArticle: PropTypes.func,
  
  // Estatísticas para MainToolbar
  totalItems: PropTypes.number,
  filteredItems: PropTypes.number,
  selectedItems: PropTypes.number,

  // Props para filtro de período
  timePeriod: PropTypes.string,
  onTimePeriodChange: PropTypes.func,
  quickDateType: PropTypes.oneOf(['published', 'created']),
  onQuickDateTypeChange: PropTypes.func,
  totalCount: PropTypes.number,

  // AdvancedFilters props
  dateValue: PropTypes.string,
  onDateChange: PropTypes.func.isRequired,
  dateOptions: PropTypes.array,
  selectedSources: PropTypes.array,
  onSourcesChange: PropTypes.func.isRequired,
  sourceOptions: PropTypes.array.isRequired,
  selectedSentiments: PropTypes.array,
  onSentimentsChange: PropTypes.func.isRequired,
  sentimentOptions: PropTypes.array,
  selectedExposures: PropTypes.array,
  onExposuresChange: PropTypes.func,
  exposureOptions: PropTypes.array,
  selectedTags: PropTypes.array,
  onTagsChange: PropTypes.func.isRequired,
  tagOptions: PropTypes.array.isRequired,
  selectedCollections: PropTypes.array,
  onCollectionsChange: PropTypes.func.isRequired,
  collectionOptions: PropTypes.array.isRequired,
  selectedMediaTypes: PropTypes.array,
  onMediaTypesChange: PropTypes.func.isRequired,
  mediaTypeOptions: PropTypes.array.isRequired,
  selectedContentTypes: PropTypes.array,
  onContentTypesChange: PropTypes.func,
  contentTypeOptions: PropTypes.array,
  selectedTiers: PropTypes.array,
  onTiersChange: PropTypes.func.isRequired,
  tierOptions: PropTypes.array.isRequired,
  selectedStatuses: PropTypes.array,
  onStatusesChange: PropTypes.func,
  statusOptions: PropTypes.array,
  showTagFilter: PropTypes.bool,
  showCollectionFilter: PropTypes.bool,
  onClearFilters: PropTypes.func,

  // Props para filtros facetados
  facets: PropTypes.object,
  facetsLoading: PropTypes.bool,
  
  // ActiveFiltersIndicator props
  activeFilters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  onRemoveFilter: PropTypes.func,
  activeFiltersExpanded: PropTypes.bool,
  onToggleActiveFiltersExpanded: PropTypes.func,
  
  // SelectionToolbar props
  onSelectAll: PropTypes.func.isRequired,
  onEditFields: PropTypes.func,
  onNativeReview: PropTypes.func,
  onFavorite: PropTypes.func.isRequired,
  onAddTag: PropTypes.func.isRequired,
  onAddToCollection: PropTypes.func.isRequired,
  onRemoveFromCollections: PropTypes.func,
  onSetSentiment: PropTypes.func.isRequired,
  onSetRead: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSendNewsletter: PropTypes.func,
  onBulkRescrapeSmart: PropTypes.func,
  onBulkAnalyzeKpia: PropTypes.func,
  onClearSelection: PropTypes.func.isRequired,
  selectedCount: PropTypes.number,
  allSelected: PropTypes.bool,
  showRemoveFromTag: PropTypes.bool,
  onRemoveFromTag: PropTypes.func,
  showRemoveFromCollection: PropTypes.bool,
  onRemoveFromCollection: PropTypes.func,
  onRemoveTags: PropTypes.func,

  // Props para seleção estilo Gmail
  currentPageCount: PropTypes.number,
  onSelectAllPages: PropTypes.func,
  selectingAllPages: PropTypes.bool,
  bulkActionLoading: PropTypes.bool,
  bulkActionProgress: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
    percentage: PropTypes.number,
    message: PropTypes.string
  }),
  exportingExcel: PropTypes.bool,

  // Conteúdo principal
  loading: PropTypes.bool,
  error: PropTypes.string,
  children: PropTypes.node,

  className: PropTypes.string
};

export default ArticlePageTemplate;
